import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Users, Building2, AlertCircle } from 'lucide-react';
import { joinApi, authApi, saveTokens, type JoinInfo } from '@/lib/api';

export function Join() {
  const { membershipId } = useParams<{ membershipId: string }>();
  const navigate = useNavigate();

  const [joinInfo, setJoinInfo] = useState<JoinInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadJoinInfo();
  }, [membershipId]);

  const loadJoinInfo = async () => {
    if (!membershipId) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    const response = await joinApi.getInfo(membershipId);

    if (response.error) {
      setError(response.error);
    } else if (response.data) {
      setJoinInfo(response.data);
    }

    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);

    const response = await joinApi.accept(membershipId!, password);

    setSubmitting(false);

    if (response.error) {
      setSubmitError(response.error);
      return;
    }

    if (response.data) {
      saveTokens(response.data.tokens);
      navigate(`/${response.data.business_id}/dashboard`);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setSubmitting(true);
      setSubmitError('');
      const response = await authApi.googleLogin({
        access_token: tokenResponse.access_token,
      });
      setSubmitting(false);

      if (response.error) {
        setSubmitError(response.error);
        return;
      }

      if (response.data) {
        saveTokens(response.data.tokens);
        navigate(`/${response.data.user.businesses[0]?.id || ''}/dashboard`);
      }
    },
    onError: () => {
      setSubmitError('Google signup failed. Please try again.');
    },
  });

  const handleGoogleSignup = async () => {
    if (!membershipId) return;
    // Store invite in session before OAuth
    await authApi.setInviteSession(membershipId);
    googleLogin();
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'full_access': return 'Full Access';
      case 'view_only': return 'View Only';
      default: return role;
    }
  };

  if (loading) return <div className="min-h-screen bg-background" />;

  if (error || !joinInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/5 p-8">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">
              {error || 'This invitation link is invalid or has expired.'}
            </p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/5 p-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* InvoiceOwl Branding */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-primary">InvoiceOwl</h1>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="text-center pb-2 space-y-4">
            {/* Business Logo/Avatar */}
            <div className="flex justify-center">
              {joinInfo.business_logo ? (
                <img
                  src={joinInfo.business_logo}
                  alt={joinInfo.business_name}
                  className="w-20 h-20 rounded-xl object-cover shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center shadow-lg">
                  <Building2 className="h-10 w-10 text-primary" />
                </div>
              )}
            </div>

            {/* Welcome Message */}
            <div>
              <p className="text-muted-foreground text-sm mb-1">You've been invited to join</p>
              <h2 className="text-2xl font-bold">{joinInfo.business_name}</h2>
            </div>

            {/* Invited User Info */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{joinInfo.invited_name}</p>
                  <p className="text-sm text-muted-foreground">{joinInfo.invited_email}</p>
                </div>
                <Badge variant="secondary" className="ml-auto">
                  {getRoleLabel(joinInfo.role)}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Social Sign Up Options */}
            <div>
              <Button
                variant="outline"
                className="w-full"
                type="button"
                onClick={handleGoogleSignup}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </Button>
            </div>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  Or sign up with email
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Create Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={4}
                />
              </div>

              {submitError && (
                <p className="text-sm text-destructive">{submitError}</p>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Creating account...' : 'Join Team'}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <a href="/login" className="text-primary hover:underline font-medium">
                Sign in
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          By joining, you agree to InvoiceOwl's Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
